import Button from '@material-ui/core/Button'
import { useContext } from 'react'
import { Modal, ModalProps } from 'react-bootstrap'
import { formatFileSizeLabel } from '../../utils/fileSize'
import { AppContext } from '../AppContext'

import './index.css'

export interface HelpProps extends ModalProps {
	onHide: () => void
}

export const Help = (props: HelpProps) => {
	const { clientConfig } = useContext(AppContext)
	return (
		<Modal show={true} onHide={props.onHide} id="help-modal">
			<Modal.Header closeButton>
				<span className="mark">
					<svg viewBox="0 0 24 24">
						<path d="M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z"></path>
					</svg>
				</span>
				<Modal.Title>
					<h2 className="f4 fw6 mb2">How ink2html works</h2>
					<p className="f6 mb0">A quick guide to transcribing your notes</p>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<article className="ph2">
					<section className="pb3 bb-light-grey">
						<h3>
							<svg viewBox="0 0 24 24">
								<path d="M13.13 22.19L11.5 18.36C13.07 17.78 14.54 17 15.9 16.09L13.13 22.19M5.64 12.5L1.81 10.87L7.91 8.1C7 9.46 6.22 10.93 5.64 12.5M21.61 2.39C21.61 2.39 16.66 .269 11 5.93C8.81 8.12 7.5 10.53 6.65 12.64C6.37 13.39 6.56 14.21 7.11 14.77L9.24 16.89C9.79 17.45 10.61 17.63 11.36 17.35C13.5 16.53 15.88 15.19 18.07 13C23.73 7.34 21.61 2.39 21.61 2.39M14.54 9.46C13.76 8.68 13.76 7.41 14.54 6.63S16.59 5.85 17.37 6.63 18.15 8.68 17.37 9.46C16.59 10.24 15.32 10.24 14.54 9.46M8.88 16.53L7.47 15.12L8.88 16.53M6.24 22L9.88 18.36C9.54 18.27 9.21 18.12 8.91 17.91L4.83 22H6.24M2 22H3.41L8.18 17.24L6.76 15.83L2 20.59V22M2 19.17L6.09 15.09C5.88 14.79 5.73 14.46 5.64 14.12L2 17.76V19.17Z"></path>
							</svg>
							Getting started
						</h3>
						<ol className="steps">
							<li>
								<span>
									Pick a <strong>transcription level</strong>:
									<dl className="level-list level-inline">
										<div className="level">
											<dt>Exact</dt>
											<dd>Verbatim — your notes, digitized as written with no rewording.</dd>
										</div>
										<div className="level">
											<dt>Natural</dt>
											<dd>Lightly cleaned up for clarity, keeping your meaning and flow.</dd>
										</div>
										<div className="level">
											<dt>Detailed</dt>
											<dd>Expands shorthand into full explanations — best for studying.</dd>
										</div>
									</dl>
								</span>
							</li>
							<li>
								<span>
									<strong>Upload</strong> a photo or PDF of your notes, or drag it onto the dropzone.
								</span>
							</li>
							<li>
								<span>
									Wait while ink2html <strong>digitizes</strong> the page. It can take under a minute
									for small documents with few images. Longer documents, or ones with more images,
									take more time.
								</span>
							</li>
							<li>
								<span>
									Review the result, then{' '}
									<strong>
										<a href="#fig-section">edit any figures</a>
									</strong>{' '}
									and their alt text and/or captions.
								</span>
							</li>
							<li>
								<span>
									<strong>Download</strong> your clean, accessible HTML.
								</span>
							</li>
						</ol>
					</section>

					<section className="pv3 bb-light-grey">
						<h3>
							<svg viewBox="0 0 24 24">
								<path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,8L14,2M18,20H6V4H13V9H18V20Z"></path>
							</svg>
							File &amp; size limits
						</h3>
						<dl className="spec">
							<div>
								<dt>Accepted formats</dt>
								<dd>JPG · PNG · WEBP· PDF</dd>
							</div>
							<div>
								<dt>Max file size</dt>
								<dd>{formatFileSizeLabel(clientConfig.maxFileSizeBytes)}</dd>
							</div>
							<div>
								<dt>Max PDF page count</dt>
								<dd>{clientConfig.maxPageCount} pages</dd>
							</div>
						</dl>
					</section>

					<section className="pv3 bb-light-grey">
						<h3>
							<svg viewBox="0 0 24 24">
								<path d="M5,9.5L7.5,14H2.5L5,9.5M3,16H7V18H3V16M3,20H7V22H3V20M9,7H21V9H9V7M9,11H21V13H9V11M9,15H21V17H9V15M9,19H21V21H9V19Z"></path>
							</svg>
							Token limits &amp; usage
						</h3>
						<p className="f6 mb2">
							Each transcription draws from a pool of <strong>tokens</strong> — denser pages and lots of
							figures use more than a single page of plain notes.
						</p>
						<p className="f6 mb2">
							Your token usage is measured over a 1 hour sliding window, and the meter in the header shows
							how much of your limit you have used.
						</p>
						<p className="f6 mb2">
							If you reach the limit, new uploads pause. You can still work on your current document to
							redraw images with AI and regenerate image alt text and captions, as well as download the
							HTML. Uploading and working on a new document is blocked until you’re back under the usage
							limit.
						</p>
						<p className="f6 mb0">
							If you feel that you’re running into the limit too often, email us at{' '}
							<a href="mailto:tlt@purdue.edu">tlt@purdue.edu</a>.
						</p>
					</section>

					<section id="fig-section" className="pv3 bb-light-grey">
						<h3>
							<svg viewBox="0 0 24 24">
								<path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z"></path>
							</svg>
							Figures &amp; image editing
						</h3>
						<p className="note purple mb2">
							Diagrams and sketches are detected automatically and listed as editable{' '}
							<strong>figures</strong>. Open any one in the <strong>Figure Editor</strong> to refine it
							before export.
						</p>
						<ul className="tips bullets">
							<li>
								<strong>Recreate with AI</strong> redraws a messy sketch as a clean digital diagram.
							</li>
							<li>
								<strong>Crop</strong> to trim stray marks or frame the figure.
							</li>
							<li>
								Toggle between your <strong>Original</strong> and the <strong>AI Digitized</strong>{' '}
								version anytime.
							</li>
							<li>
								Edit or regenerate each figure’s <strong>alt text</strong> and <strong>caption</strong>{' '}
								in place.
							</li>
						</ul>
					</section>

					<section className="pv3 bb-light-grey">
						<h3>
							<svg viewBox="0 0 24 24">
								<path d="M12,2A2,2 0 0,1 14,4A2,2 0 0,1 12,6A2,2 0 0,1 10,4A2,2 0 0,1 12,2M21,9H15V22H13V16H11V22H9V9H3V7H21V9Z"></path>
							</svg>
							Accessibility &amp; output
						</h3>
						<ul className="tips bullets">
							<li>Clean, semantic HTML that works with screen readers.</li>
							<li>
								Auto-generated <strong>alt text</strong> for every figure — editable before you export.
							</li>
							<li>
								Descriptive <strong>captions</strong> you can adjust or rewrite in place.
							</li>
							<li>
								Files are built for Brightspace, so the <strong>H1 heading is omitted</strong>{' '}
								(Brightspace supplies it from the topic title). If you use a file elsewhere,{' '}
								<a href="#using-files">add an H1 manually</a>.
							</li>
						</ul>
						<p className="note warn mv2">
							If a region is too faint to read, ink2html marks it <strong>“Could Not Transcribe”</strong>{' '}
							rather than guessing. Re-upload a clearer scan and run the document again to retry.
						</p>
					</section>

					<section className="pv3 bb-light-grey">
						<h3>
							<svg viewBox="0 0 24 24">
								<path d="M12,2A7,7 0 0,1 19,9C19,11.38 17.81,13.47 16,14.74V17A1,1 0 0,1 15,18H9A1,1 0 0,1 8,17V14.74C6.19,13.47 5,11.38 5,9A7,7 0 0,1 12,2M9,21V20H15V21A1,1 0 0,1 14,22H10A1,1 0 0,1 9,21Z"></path>
							</svg>
							Tips for the best results
						</h3>
						<div className="tip-cards">
							<div className="tip-card">
								<h4>
									<svg viewBox="0 0 24 24">
										<path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
									</svg>
									Good lighting
								</h4>
								<p>
									Shoot the page flat in even light with no shadows. Dark ink on light paper reads
									most accurately.
								</p>
							</div>
							<div className="tip-card">
								<h4>
									<svg viewBox="0 0 24 24">
										<path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
									</svg>
									Keep it short
								</h4>
								<p>
									For very long documents, upload a handful of pages at a time. Smaller batches
									process faster and more reliably.
								</p>
							</div>
							<div className="tip-card">
								<h4>
									<svg viewBox="0 0 24 24">
										<path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
									</svg>
									Dense pages
								</h4>
								<p>
									Use a high-resolution scan when notes are packed tight, so text and diagrams stay
									easy to tell apart.
								</p>
							</div>
							<div className="tip-card">
								<h4>
									<svg viewBox="0 0 24 24">
										<path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
									</svg>
									Crop first
								</h4>
								<p>
									Trim away edges and clutter before uploading so the AI focuses on just your notes.
								</p>
							</div>
						</div>
					</section>

					<section id="using-files" className="pt3 bb-0">
						<h3>
							<svg viewBox="0 0 24 24">
								<path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,8L14,2M18,20H6V4H13V9H18V20M16,11H8V13H16V11M16,15H8V17H16V15Z"></path>
							</svg>
							Using your files
						</h3>

						<h4 className="ttu f7 fw7 color-blue-grey mb2">In Brightspace</h4>
						<ol className="steps">
							<li>
								<span>
									In your Brightspace course, go to <strong>Content</strong> and open the module where
									you want the notes.
								</span>
							</li>
							<li>
								<span>
									Select <strong>Upload / Create → Upload Files</strong>.
								</span>
							</li>
							<li>
								<span>
									Select <strong>My Computer</strong>, click <strong>Upload</strong>, then choose your
									downloaded <strong>.html</strong> file.
								</span>
							</li>
							<li>
								<span>
									Brightspace adds it as a topic that opens as its own page — students see the
									formatting, figures, and math intact.
								</span>
							</li>
						</ol>
						<p className="note purple mv2">
							The page’s <strong>H1 heading</strong> automatically defaults to the name of the file you
							upload. To change it, click the topic link, then the arrow next to its name and select{' '}
							<strong>Edit Title</strong>.
						</p>
						<p className="note warn mb2">
							Upload the <strong>.html file itself</strong> — don't paste the contents into the
							Brightspace HTML editor, which strips the styling and layout.
						</p>

						<h4 className="ttu f7 fw7 color-blue-grey mt3 mb2">Outside Brightspace</h4>
						<p className="note plain mb2">
							The file is a complete web page — open it in any browser, or host it on a website or other
							LMS, and the formatting, figures, and math display the same.
						</p>
						<p className="note warn mb0">
							These files omit the <strong>H1 heading</strong> (Brightspace supplies it from the topic
							title). Outside Brightspace, add your own H1 near the top of the <code>&lt;body&gt;</code> —
							e.g. <code>&lt;h1&gt;Your title&lt;/h1&gt;</code> — so the page has a proper main heading
							for accessibility.
						</p>
					</section>
				</article>
			</Modal.Body>
			<Modal.Footer>
				<Button className="btn-primary ttc fr mb1" color="primary" onClick={props.onHide}>
					Got it!
				</Button>
			</Modal.Footer>
		</Modal>
	)
}
